import React, { memo, useCallback, useMemo } from 'react';
import { View, StyleSheet } from 'react-native';
import Text from 'design-system/Text';
import Spacer from 'design-system/Spacer';
import { rem } from 'design-system/values';
import { CardView } from 'design-system/CardView';
import SectionHeader from 'design-system/SectionHeader';
import BuySellHold from 'design-system/BuySellHoldView';
import CircularProgress from 'design-system/CircularProgress';
import { TextButtonLinkSeeAll } from 'design-system/TextButton';
import useColors from 'hooks/useColors';
import { formatAmount, formatPlural } from 'utils/formatting';
import { useMainStackNavigation } from 'utils/types/navigationV6';
const strings = {
    toolTipTitle: 'Analyst ratings',
    toolTipSubTitle: 'Analyst ratings show Buy, Hold, and Sell recommendations from analysts at third party brokers. \n\nThese ratings are opinions on the stock’s future performance based on independent analysis on the company’s financials (e.g. sales, profit, debt and growth).\n\nThese ratings should not be seen as investment advice. However, the more resources and data you take into account, the more informed your investment decisions may be. \n\nData is sourced by Refinitiv.',
};
const AnalystRatings = ({ assetId, buyPercent, holdPercent, sellPercent, ratingsTotal, }) => {
    const colors = useColors();
    const navigation = useMainStackNavigation();
    const circularProgressInfo = useMemo(() => {
        if (buyPercent === undefined || sellPercent === undefined || holdPercent === undefined) {
            return undefined;
        }
        let progressInfo = {
            percent: buyPercent,
            action: 'Buy',
            color: colors.status.positive,
        };
        if (holdPercent > progressInfo.percent) {
            progressInfo = {
                percent: holdPercent,
                action: 'Hold',
                color: colors.text.tertiary,
            };
        }
        if (sellPercent > progressInfo.percent) {
            progressInfo = {
                percent: sellPercent,
                action: 'Sell',
                color: colors.elements.statusNegative,
            };
        }
        return progressInfo;
    }, [
        buyPercent,
        colors.elements.statusNegative,
        colors.status.positive,
        colors.text.tertiary,
        holdPercent,
        sellPercent,
    ]);
    const handleAnalystRatingsDetails = useCallback(() => {
        navigation.navigateDeprecated('InvestAnalystRatingsDetails', { assetId });
    }, [assetId]);
    if (!ratingsTotal ||
        buyPercent === undefined ||
        sellPercent === undefined ||
        holdPercent === undefined ||
        !circularProgressInfo) {
        return null;
    }
    return (<>
      <SectionHeader horizontalMargins largeVerticalMargins toolTipTitle={strings.toolTipTitle} toolTipSubTitle={strings.toolTipSubTitle} button={<TextButtonLinkSeeAll onPress={handleAnalystRatingsDetails}/>}>
        Analyst ratings
      </SectionHeader>
      <CardView noMarginBottom noMarginTop>
        <View style={styles.graphicsContainer}>
          <CircularProgress size={100} percent={circularProgressInfo.percent} backgroundCircleColor={colors.background.dark} progressCircleColor={circularProgressInfo.color}>
            <View style={styles.recommendedActionContainer}>
              <Text Numbers-16>{formatAmount(circularProgressInfo.percent, undefined, 2)}%</Text>
              <Text TextThin-12 Secondary>
                {circularProgressInfo.action}
              </Text>
            </View>
          </CircularProgress>
          <Spacer w={16}/>
          <BuySellHold buyPercent={buyPercent} sellPercent={sellPercent} holdPercent={holdPercent}/>
        </View>
        <View style={styles.bottomTextContainer}>
          <Text TextThin-14 Secondary>
            Ratings based on info from {ratingsTotal} {formatPlural('analyst', 'analysts', ratingsTotal)}.
          </Text>
        </View>
      </CardView>
    </>);
};
export default memo(AnalystRatings);
const styles = StyleSheet.create({
    bottomTextContainer: {
        padding: rem(16),
    },
    graphicsContainer: {
        flexDirection: 'row',
        paddingTop: rem(16),
        paddingHorizontal: rem(16),
    },
    recommendedActionContainer: {
        alignItems: 'center',
        justifyContent: 'center',
    },
});
